import { useMemo, useState } from "react";
import { ArrowRight, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { clientCategories, clientEntries, clientGalleryItems, clientRegions, clientSections, parseClientLabel } from "@/content/clientsContent";

export default function ClientsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeRegion, setActiveRegion] = useState("All");

  const filteredClients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return clientEntries.filter((entry) => {
      const searchTarget = `${entry.name} ${entry.rawName} ${entry.status || ""}`.toLowerCase();
      const matchesQuery = !query || searchTarget.includes(query);
      const matchesCategory = activeCategory === "All" || entry.category === activeCategory;
      const matchesRegion = activeRegion === "All" || entry.region === activeRegion;
      return matchesQuery && matchesCategory && matchesRegion;
    });
  }, [activeCategory, activeRegion, searchQuery]);

  return (
    <div data-testid="clients-page">
      <section className="bg-slate-950 py-24 text-white md:py-28" data-testid="clients-hero-section">
        <div className="mx-auto max-w-6xl px-6 md:px-12">
          <div className="max-w-4xl space-y-6">
            <div className="section-kicker text-orange-400" data-testid="clients-hero-kicker">Clients</div>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="clients-hero-heading">
              Trusted clients across food service, hospitality, institutional, and religious establishment projects.
            </h1>
            <p className="text-base text-slate-300 md:text-lg" data-testid="clients-hero-description">
              Explore Concept Magik's client portfolio across restaurant consulting, hotel food service design, institutional dining, corporate facilities, and religious establishment work in the USA and international markets.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid="clients-hero-stats-grid">
            {[
              { label: "Client Groups", value: `${clientSections.length}` },
              { label: "Named Clients", value: `${clientEntries.length}+` },
              { label: "Regions", value: "USA • India • Bahrain • UAE • Canada" },
            ].map((item) => (
              <div key={item.label} className="border border-white/10 bg-white/5 p-6" data-testid={`clients-hero-stat-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="text-sm uppercase tracking-[0.24em] text-slate-400">{item.label}</div>
                <div className="mt-3 text-2xl font-bold text-white">{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 md:py-28" data-testid="clients-directory-section">
        <div className="mx-auto max-w-7xl space-y-10 px-6 md:px-12">
          <div className="grid gap-6 border border-slate-200 bg-slate-50 p-6 md:grid-cols-[1.2fr_0.8fr_0.8fr] md:p-8" data-testid="clients-filter-panel">
            <label className="grid gap-2" data-testid="clients-search-field">
              <span className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Search clients</span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  className="w-full border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-orange-600"
                  data-testid="clients-search-input"
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by client name, location, or project"
                  value={searchQuery}
                />
              </div>
            </label>

            <label className="grid gap-2" data-testid="clients-category-field">
              <span className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Category</span>
              <select
                className="border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-orange-600"
                data-testid="clients-category-select"
                onChange={(event) => setActiveCategory(event.target.value)}
                value={activeCategory}
              >
                {clientCategories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </label>

            <label className="grid gap-2" data-testid="clients-region-field">
              <span className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Region</span>
              <select
                className="border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-orange-600"
                data-testid="clients-region-select"
                onChange={(event) => setActiveRegion(event.target.value)}
                value={activeRegion}
              >
                {clientRegions.map((region) => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 md:flex-row md:items-end md:justify-between">
            <div className="space-y-3">
              <div className="section-kicker" data-testid="clients-directory-kicker">Client directory</div>
              <h2 className="text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl" data-testid="clients-directory-heading">
                Browse our client portfolio by sector, region, and project footprint.
              </h2>
            </div>
            <div className="text-sm text-slate-500" data-testid="clients-results-count">
              Showing <span className="font-bold text-slate-950">{filteredClients.length}</span> of {clientEntries.length} clients
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="clients-results-grid">
            {filteredClients.map((client) => (
              <article
                key={client.id}
                className="group border border-slate-200 bg-white p-6 transition-colors duration-200 hover:border-orange-500 hover:bg-orange-50/40"
                data-testid={`client-directory-card-${client.id}`}
              >
                <div className="mb-5 flex flex-wrap items-center gap-2">
                  <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-600" data-testid={`client-directory-category-${client.id}`}>
                    {client.category}
                  </span>
                  <span className="inline-flex rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-orange-700" data-testid={`client-directory-region-${client.id}`}>
                    {client.region}
                  </span>
                  {client.status ? (
                    <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700" data-testid={`client-directory-status-${client.id}`}>
                      {client.status}
                    </span>
                  ) : null}
                </div>
                <h3 className="text-2xl font-bold leading-tight tracking-tight text-slate-950" data-testid={`client-directory-name-${client.id}`}>
                  {client.name}
                </h3>
              </article>
            ))}
          </div>

          {filteredClients.length === 0 ? (
            <div className="border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500" data-testid="clients-empty-state">
              No clients match your current search and filters.
            </div>
          ) : null}
        </div>
      </section>

      <section className="bg-slate-50 py-24 md:py-28" data-testid="clients-gallery-section">
        <div className="mx-auto max-w-7xl space-y-8 px-6 md:px-12">
          <div className="max-w-3xl space-y-4">
            <div className="section-kicker" data-testid="clients-gallery-kicker">Project gallery</div>
            <h2 className="text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl" data-testid="clients-gallery-heading">
              Selected project imagery from food service and facility design work.
            </h2>
            <p className="text-base text-slate-600 md:text-lg" data-testid="clients-gallery-description">
              This gallery presents a broader visual record of consulting, planning, kitchen design, and facility development work while the client directory remains separate for clarity.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="clients-gallery-grid">
            {clientGalleryItems.map((item, index) => (
              <figure
                key={item.id}
                className={`overflow-hidden border border-slate-200 bg-white ${index % 7 === 0 ? "xl:col-span-2" : ""}`}
                data-testid={`clients-gallery-item-${item.id}`}
              >
                <img
                  alt={item.title}
                  className="aspect-[4/3] w-full object-cover object-center"
                  data-testid={`clients-gallery-image-${item.id}`}
                  src={item.image}
                />
                <figcaption className="border-t border-slate-200 px-5 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-slate-700" data-testid={`clients-gallery-caption-${item.id}`}>
                  {item.title}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 md:py-28" data-testid="clients-sections-summary-section">
        <div className="mx-auto max-w-7xl space-y-10 px-6 md:px-12">
          {clientSections.map((section) => (
            <div key={section.slug} className="grid gap-4 border-t border-slate-200 pt-8 md:grid-cols-[0.42fr_0.58fr]" data-testid={`clients-section-${section.slug}`}>
              <div className="space-y-3">
                <h3 className="text-2xl font-bold text-slate-950" data-testid={`clients-section-title-${section.slug}`}>{section.title}</h3>
                <p className="text-sm text-slate-500" data-testid={`clients-section-description-${section.slug}`}>{section.description}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                {section.clients.slice(0, 8).map((client) => {
                  const parsed = parseClientLabel(client);
                  return (
                    <div
                      key={client}
                      className="inline-flex flex-wrap items-center gap-2 border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700"
                      data-testid={`clients-section-chip-${section.slug}-${client.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <span>{parsed.displayName}</span>
                      {parsed.status ? (
                        <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700" data-testid={`clients-section-chip-status-${section.slug}-${client.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {parsed.status}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-24 md:py-28" data-testid="clients-cta-section">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 border border-slate-200 bg-slate-950 px-8 py-12 text-white md:px-12 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl space-y-4">
            <div className="section-kicker text-orange-400" data-testid="clients-cta-kicker">Discuss your project</div>
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl" data-testid="clients-cta-heading">
              Looking for a consulting partner for your next food service, hospitality, or institutional project?
            </h2>
          </div>
          <Link
            className="inline-flex items-center justify-center gap-2 rounded-sm bg-orange-600 px-6 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-orange-500"
            data-testid="clients-cta-link"
            to="/contact"
          >
            Start a conversation
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
